import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { useMessageBox } from '../components/MessageBoxContainer';
import AdvancedReports from '../components/AdvancedReports';
import { getCandidateName } from '../constants/candidatesData';
import '../styles/AdminPanel.css';

const AdminPanel = () => {
  const navigate = useNavigate();
  const messageBox = useMessageBox();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('statistics');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Data states
  const [statistics, setStatistics] = useState(null);
  const [votes, setVotes] = useState([]);
  const [students, setStudents] = useState([]);
  const [voters, setVoters] = useState([]);
  const [festivalRegistrations, setFestivalRegistrations] = useState([]);
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [eventStats, setEventStats] = useState(null);
  const [tikkaRegistrations, setTikkaRegistrations] = useState([]);
  const [tikkaStats, setTikkaStats] = useState(null);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  
  // Edit modal state
  const [editingVote, setEditingVote] = useState(null);
  const [editCandidates, setEditCandidates] = useState([]);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, token, activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const loadingId = messageBox.loading('در حال ورود به پنل مدیریت...');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در ورود');
      }
      
      const data = await response.json();
      setToken(data.access_token);
      localStorage.setItem('admin_token', data.access_token);
      setIsLoggedIn(true);
      setError('');
      
      messageBox.hideMessage(loadingId);
      messageBox.success('ورود به پنل مدیریت با موفقیت انجام شد', 3000);
    } catch (err) {
      setError(err.message);
      messageBox.hideMessage(loadingId);
      messageBox.error(`<strong>خطا در ورود</strong>\n\n${err.message}\n\n• لطفاً ایمیل و رمز عبور خود را بررسی کنید`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken('');
    localStorage.removeItem('admin_token');
    navigate('/');
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'statistics') {
        const response = await fetch(`${BACKEND_URL}/api/admin/statistics`, {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStatistics(data);
        }
      } else if (activeTab === 'votes') {
        const response = await fetch(`${BACKEND_URL}/api/admin/votes`, {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setVotes(data.votes);
        }
      } else if (activeTab === 'students') {
        const response = await fetch(`${BACKEND_URL}/api/admin/students`, {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStudents(data.students);
        }
      } else if (activeTab === 'voters') {
        const response = await fetch(`${BACKEND_URL}/api/admin/voters`, {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setVoters(data.voters);
        }
      } else if (activeTab === 'festival') {
        const response = await fetch(`${BACKEND_URL}/api/landing/registrations`, {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          mode: 'cors',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setFestivalRegistrations(data);
        }
      } else if (activeTab === 'animation-event') {
        const [registrationsRes, statsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/event/registrations`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            },
            mode: 'cors',
            credentials: 'include'
          }),
          fetch(`${BACKEND_URL}/api/event/statistics`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            },
            mode: 'cors',
            credentials: 'include'
          })
        ]);
        
        if (registrationsRes.ok && statsRes.ok) {
          const registrationsData = await registrationsRes.json();
          const statsData = await statsRes.json();
          setEventRegistrations(registrationsData.registrations);
          setEventStats(statsData);
        }
      } else if (activeTab === 'tikka-event') {
        const [registrationsRes, statsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/tikka/registrations`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            },
            mode: 'cors',
            credentials: 'include'
          }),
          fetch(`${BACKEND_URL}/api/tikka/statistics`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            },
            mode: 'cors',
            credentials: 'include'
          })
        ]);
        
        if (registrationsRes.ok && statsRes.ok) {
          const registrationsData = await registrationsRes.json();
          const statsData = await statsRes.json();
          setTikkaRegistrations(registrationsData.registrations);
          setTikkaStats(statsData);
        }
      }
    } catch (err) {
      setError('خطا در دریافت اطلاعات: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVote = async (voteId) => {
    const result = await new Promise((resolve) => {
      const confirmed = window.confirm('آیا از حذف این رای اطمینان دارید؟');
      resolve(confirmed);
    });
    
    if (!result) return;
    
    const loadingId = messageBox.loading('در حال حذف رای...');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/vote/${voteId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'include'
      });
      
      if (response.ok) {
        messageBox.hideMessage(loadingId);
        messageBox.success('رای با موفقیت حذف شد', 4000);
        loadData();
      } else {
        throw new Error('خطا در حذف رای');
      }
    } catch (err) {
      messageBox.hideMessage(loadingId);
      messageBox.error('<strong>خطا در حذف رای</strong>\n\nمتأسفانه حذف رای با مشکل مواجه شد\n\n• لطفاً دوباره تلاش کنید');
    }
  };

  const handleReloadStudents = async () => {
    const confirmed = window.confirm(
      '⚠️ هشدار: این عملیات تمام دانشجویان فعلی را پاک کرده و دوباره از فایل اکسل بارگذاری می‌کند.\n\n' +
      'این عملیات برای رفع مشکل شماره ملی‌های فاقد صفر اول طراحی شده است.\n\n' +
      'آیا مطمئن هستید که می‌خواهید ادامه دهید؟'
    );
    
    if (!confirmed) return;
    
    const loadingId = messageBox.loading('در حال بارگذاری مجدد دانشجویان از فایل اکسل...\nلطفاً صبر کنید، این عملیات ممکن است چند ثانیه طول بکشد.');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reload-students`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        mode: 'cors',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'خطا در بارگذاری مجدد');
      }
      
      const data = await response.json();
      messageBox.hideMessage(loadingId);
      messageBox.success(
        `<strong>✅ بارگذاری موفقیت‌آمیز</strong>\n\n` +
        `• تعداد دانشجویان بارگذاری شده: ${data.total_students} نفر\n` +
        `• شماره ملی‌ها با صفر اول حفظ شدند\n\n` +
        `نمونه شماره ملی‌ها:\n${data.sample_national_ids.slice(0, 3).join('\n')}`,
        6000
      );
      loadData(); // Reload the students list
    } catch (err) {
      messageBox.hideMessage(loadingId);
      messageBox.error(
        `<strong>❌ خطا در بارگذاری مجدد</strong>\n\n` +
        `${err.message}\n\n` +
        `• لطفاً اتصال اینترنت خود را بررسی کنید\n` +
        `• در صورت ادامه مشکل با پشتیبانی تماس بگیرید`
      );
    }
  };


  const handleEditVote = (vote) => {
    setEditingVote(vote);
    setEditCandidates(vote.selected_candidates.join(','));
  };

  const handleSaveEdit = async () => {
    const candidatesArray = editCandidates.split(',').map(c => parseInt(c.trim())).filter(c => !isNaN(c));
    
    if (candidatesArray.length < 1 || candidatesArray.length > 7) {
      messageBox.error('<strong>تعداد نامزدها نامعتبر است</strong>\n\nتعداد نامزدها باید بین 1 تا 7 باشد');
      return;
    }

    const loadingId = messageBox.loading('در حال ذخیره تغییرات...');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/vote/${editingVote._id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'include',
        body: JSON.stringify({
          vote_id: editingVote._id,
          selected_candidates: candidatesArray
        })
      });
      
      if (response.ok) {
        messageBox.hideMessage(loadingId);
        messageBox.success('رای با موفقیت ویرایش شد', 4000);
        setEditingVote(null);
        setEditCandidates([]);
        loadData();
      } else {
        throw new Error('خطا در ویرایش رای');
      }
    } catch (err) {
      messageBox.hideMessage(loadingId);
      messageBox.error('<strong>خطا در ویرایش رای</strong>\n\nمتأسفانه ویرایش رای با مشکل مواجه شد\n\n• لطفاً دوباره تلاش کنید');
    }
  };

  const handleExportExcel = async (type) => {
    try {
      setLoading(true);
      const loadingId = messageBox.loading('در حال تولید فایل Excel...');
      
      // Determine the correct endpoint
      const endpoint = type.startsWith('event/') 
        ? `${BACKEND_URL}/api/${type}` 
        : `${BACKEND_URL}/api/admin/export/${type}`;
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        mode: 'cors',
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const filename = `${type.replace(/\//g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
        saveAs(blob, filename);
        
        messageBox.hideMessage(loadingId);
        messageBox.success(`<strong>دانلود موفقیت‌آمیز بود!</strong>\n\nفایل Excel با موفقیت دانلود شد\n\n• نام فایل: ${filename}`, 4000);
      } else {
        throw new Error('خطا در دانلود فایل');
      }
    } catch (err) {
      messageBox.error(`<strong>خطا در تولید فایل Excel</strong>\n\n${err.message}\n\n• لطفاً دوباره تلاش کنید`);
    } finally {
      setLoading(false);
    }
  };

  // Filter functions
  const filterVotes = () => {
    let filtered = votes;
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(vote => 
        vote.student_id.includes(searchTerm) ||
        vote.student_name.includes(searchTerm) ||
        vote.student_major.includes(searchTerm)
      );
    }
    
    // Date filter
    if (dateFilter.start) {
      filtered = filtered.filter(vote => 
        new Date(vote.voted_at) >= new Date(dateFilter.start)
      );
    }
    if (dateFilter.end) {
      filtered = filtered.filter(vote => 
        new Date(vote.voted_at) <= new Date(dateFilter.end + 'T23:59:59')
      );
    }
    
    return filtered;
  };

  const filterVoters = () => {
    let filtered = voters;
    
    if (searchTerm) {
      filtered = filtered.filter(voter => 
        voter.student_id.includes(searchTerm) ||
        voter.student_name.includes(searchTerm) ||
        voter.major.includes(searchTerm)
      );
    }
    
    if (dateFilter.start) {
      filtered = filtered.filter(voter => 
        new Date(voter.voted_at) >= new Date(dateFilter.start)
      );
    }
    if (dateFilter.end) {
      filtered = filtered.filter(voter => 
        new Date(voter.voted_at) <= new Date(dateFilter.end + 'T23:59:59')
      );
    }
    
    return filtered;
  };

  const filterStudents = () => {
    if (!searchTerm) return students;
    
    return students.filter(student => 
      student.student_id.includes(searchTerm) ||
      student.name.includes(searchTerm) ||
      student.family.includes(searchTerm) ||
      student.major.includes(searchTerm) ||
      student.national_id.includes(searchTerm)
    );
  };

  // Translation functions for faculty and major codes
  const translateFacultyCode = (code) => {
    const translations = {
      'engineering': 'فنی و مهندسی',
      'humanities': 'علوم انسانی',
      'sports': 'علوم ورزشی'
    };
    return translations[code] || code;
  };

  const translateMajorCode = (code) => {
    const translations = {
      'computer': 'مهندسی کامپیوتر',
      'chemical': 'مهندسی شیمی',
      'architecture': 'مهندسی معماری',
      'electrical': 'مهندسی برق',
      'civil': 'مهندسی عمران',
      'mechanical': 'مهندسی مکانیک',
      'industrial': 'مهندسی صنایع',
      'surveying': 'مهندسی نقشه برداری',
      'biomedical': 'برق-پزشکی',
      'environment': 'مهندسی محیط زیست',
      'management': 'مدیریت',
      'psychology': 'روانشناسی',
      'accounting': 'حسابداری',
      'law': 'حقوق',
      'physical': 'تربیت بدنی و علوم ورزشی'
    };
    return translations[code] || code;
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login-page" data-testid="admin-login-page">
        <div className="login-container">
          <div className="login-header">
            <i className="fas fa-shield-alt"></i>
            <h1>پنل مدیریت</h1>
            <p>سامانه رای‌گیری الکترونیک دانشگاه شمال آمل</p>
          </div>
          
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="email">
                <i className="fas fa-envelope"></i>
                ایمیل
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="shomalanjoman.admin@shomal.ir"
                required
                data-testid="admin-email-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">
                <i className="fas fa-lock"></i>
                رمز عبور
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="رمز عبور خود را وارد کنید"
                required
                data-testid="admin-password-input"
              />
            </div>
            
            {error && (
              <div className="error-message" data-testid="login-error">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}
            
            <button 
              type="submit" 
              className="btn-login"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? (
                <><i className="fas fa-spinner fa-spin"></i> در حال ورود...</>
              ) : (
                <><i className="fas fa-sign-in-alt"></i> ورود به پنل</>  
              )}
            </button>
          </form>
          
          <button 
            className="back-to-home"
            onClick={() => navigate('/')}
            data-testid="back-home-btn"
          >
            <i className="fas fa-home"></i>
            بازگشت به صفحه اصلی
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <div className="admin-header">
        <div className="header-content">
          <div className="header-title">
            <i className="fas fa-chart-line"></i>
            <h1>پنل مدیریت انتخابات</h1>
          </div>
          <button className="btn-logout" onClick={handleLogout} data-testid="logout-btn">
            <i className="fas fa-sign-out-alt"></i>
            خروج
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
          data-testid="tab-statistics"
        >
          <i className="fas fa-chart-pie"></i>
          آمار کلی
        </button>
        <button 
          className={`tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
          data-testid="tab-reports"
        >
          <i className="fas fa-chart-line"></i>
          گزارشات پیشرفته
        </button>
        <button 
          className={`tab ${activeTab === 'votes' ? 'active' : ''}`}
          onClick={() => setActiveTab('votes')}
          data-testid="tab-votes"
        >
          <i className="fas fa-vote-yea"></i>
          رای‌ها
        </button>
        <button 
          className={`tab ${activeTab === 'voters' ? 'active' : ''}`}
          onClick={() => setActiveTab('voters')}
          data-testid="tab-voters"
        >
          <i className="fas fa-users"></i>
          رای دهندگان
        </button>
        <button 
          className={`tab ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
          data-testid="tab-students"
        >
          <i className="fas fa-user-graduate"></i>
          دانشجویان
        </button>
        <button 
          className={`tab ${activeTab === 'festival' ? 'active' : ''}`}
          onClick={() => setActiveTab('festival')}
          data-testid="tab-festival"
        >
          <i className="fas fa-gift"></i>
          ثبت نامی جشن
        </button>
        <button 
          className={`tab ${activeTab === 'animation-event' ? 'active' : ''}`}
          onClick={() => setActiveTab('animation-event')}
          data-testid="tab-animation-event"
        >
          <i className="fas fa-film"></i>
          رویداد انیمیشن
        </button>
        <button 
          className={`tab ${activeTab === 'tikka-event' ? 'active' : ''}`}
          onClick={() => setActiveTab('tikka-event')}
          data-testid="tab-tikka-event"
        >
          <i className="fas fa-rocket"></i>
          رویداد تیکا
        </button>
      </div>

      <div className="admin-content">
        {loading && (
          <div className="loading-overlay">
            <i className="fas fa-spinner fa-spin"></i>
            <p>در حال بارگذاری...</p>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

        {activeTab === 'statistics' && statistics && (
          <div className="statistics-section" data-testid="statistics-section">
            <div className="section-header" style={{marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <h2 style={{margin: 0}}>آمار کلی</h2>
              <button 
                className="btn-export"
                onClick={() => handleExportExcel('statistics')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-file-excel"></i>
                دانلود Excel
              </button>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <i className="fas fa-users"></i>
                <h3>کل دانشجویان</h3>
                <p className="stat-number">{statistics.total_students.toLocaleString()}</p>
              </div>
              <div className="stat-card">
                <i className="fas fa-vote-yea"></i>
                <h3>تعداد رای‌ها</h3>
                <p className="stat-number">{statistics.total_votes.toLocaleString()}</p>
              </div>
              <div className="stat-card">
                <i className="fas fa-percentage"></i>
                <h3>میزان مشارکت</h3>
                <p className="stat-number">{statistics.participation_rate}%</p>
              </div>
            </div>

            <div className="detailed-stats">
              <div className="stat-section">
                <h3><i className="fas fa-building"></i> آمار بر اساس دانشکده</h3>
                <div className="stat-list">
                  {statistics.faculty_stats.map((item, index) => (
                    <div key={index} className="stat-item">
                      <span>{translateFacultyCode(item._id) || 'نامشخص'}</span>
                      <strong>{item.count} رای</strong>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stat-section">
                <h3><i className="fas fa-graduation-cap"></i> آمار بر اساس رشته</h3>
                <div className="stat-list">
                  {statistics.major_stats.map((item, index) => (
                    <div key={index} className="stat-item">
                      <span>{translateMajorCode(item._id) || 'نامشخص'}</span>
                      <strong>{item.count} رای</strong>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stat-section">
                <h3><i className="fas fa-user-tie"></i> آمار رای نامزدها</h3>
                <div className="stat-list">
                  {Object.entries(statistics.candidate_votes)
                    .sort((a, b) => b[1] - a[1])
                    .map(([candidateId, count]) => (
                      <div key={candidateId} className="stat-item">
                        <span>{getCandidateName(Number(candidateId))}</span>
                        <strong>{count} رای</strong>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <AdvancedReports token={token} backendUrl={BACKEND_URL} />
        )}

        {activeTab === 'votes' && (
          <div className="votes-section" data-testid="votes-section">
            <div className="section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <div>
                <h2>لیست کامل رای‌ها</h2>
                <p>تعداد کل: {filterVotes().length} رای</p>
              </div>
              <button 
                className="btn-export"
                onClick={() => handleExportExcel('votes')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-file-excel"></i>
                دانلود Excel
              </button>
            </div>

            {/* Search and Filter */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px'
            }}>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-search"></i> جستجو
                </label>
                <input
                  type="text"
                  placeholder="شماره دانشجویی، نام، رشته..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-calendar"></i> از تاریخ
                </label>
                <input
                  type="date"
                  value={dateFilter.start}
                  onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-calendar"></i> تا تاریخ
                </label>
                <input
                  type="date"
                  value={dateFilter.end}
                  onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{display: 'flex', alignItems: 'flex-end'}}>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setDateFilter({start: '', end: ''});
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <i className="fas fa-times"></i> پاک کردن فیلتر
                </button>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>شماره دانشجویی</th>
                    <th>نام دانشجو</th>
                    <th>رشته</th>
                    <th>مقطع</th>
                    <th>تعداد نامزدها</th>
                    <th>نامزدهای انتخابی</th>
                    <th>تاریخ رای</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {filterVotes().map((vote) => (
                    <tr key={vote._id}>
                      <td>{vote.student_id}</td>
                      <td>{vote.student_name}</td>
                      <td>{vote.student_major}</td>
                      <td>{vote.student_level}</td>
                      <td>{vote.selected_candidates.length}</td>
                      <td>{vote.selected_candidates.join(', ')}</td>
                      <td>{new Date(vote.voted_at).toLocaleString('fa-IR')}</td>
                      <td>
                        <div style={{display: 'flex', gap: '8px', justifyContent: 'center'}}>
                          <button 
                            className="btn-edit"
                            onClick={() => handleEditVote(vote)}
                            data-testid={`edit-vote-${vote._id}`}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            <i className="fas fa-edit"></i> ویرایش
                          </button>
                          <button 
                            className="btn-delete"
                            onClick={() => handleDeleteVote(vote._id)}
                            data-testid={`delete-vote-${vote._id}`}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            <i className="fas fa-trash"></i> حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'voters' && (
          <div className="voters-section" data-testid="voters-section">
            <div className="section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <div>
                <h2>لیست رای دهندگان</h2>
                <p>تعداد کل: {filterVoters().length} نفر</p>
              </div>
              <button 
                className="btn-export"
                onClick={() => handleExportExcel('voters')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-file-excel"></i>
                دانلود Excel
              </button>
            </div>

            {/* Search and Filter */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px'
            }}>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-search"></i> جستجو
                </label>
                <input
                  type="text"
                  placeholder="شماره دانشجویی، نام، رشته..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-calendar"></i> از تاریخ
                </label>
                <input
                  type="date"
                  value={dateFilter.start}
                  onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-calendar"></i> تا تاریخ
                </label>
                <input
                  type="date"
                  value={dateFilter.end}
                  onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{display: 'flex', alignItems: 'flex-end'}}>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setDateFilter({start: '', end: ''});
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <i className="fas fa-times"></i> پاک کردن فیلتر
                </button>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>شماره دانشجویی</th>
                    <th>نام و نام خانوادگی</th>
                    <th>رشته</th>
                    <th>مقطع</th>
                    <th>تعداد نامزدها</th>
                    <th>تاریخ رای</th>
                  </tr>
                </thead>
                <tbody>
                  {filterVoters().map((voter, index) => (
                    <tr key={index}>
                      <td>{voter.student_id}</td>
                      <td>{voter.student_name}</td>
                      <td>{voter.major}</td>
                      <td>{voter.level}</td>
                      <td>{voter.candidates_count}</td>
                      <td>{new Date(voter.voted_at).toLocaleString('fa-IR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'students' && (
          <div className="students-section" data-testid="students-section">
            <div className="section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <div>
                <h2>لیست دانشجویان</h2>
                <p>تعداد کل: {filterStudents().length} نفر</p>
              </div>
              <div style={{display: 'flex', gap: '10px'}}>
                <button 
                  className="btn-reload"
                  onClick={handleReloadStudents}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#F59E0B',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '600'
                  }}
                  data-testid="reload-students-btn"
                >
                  <i className="fas fa-sync-alt"></i>
                  بارگذاری مجدد از اکسل
                </button>
                <button 
                  className="btn-export"
                  onClick={() => handleExportExcel('students')}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="fas fa-file-excel"></i>
                  دانلود Excel
                </button>
              </div>
            </div>

            {/* Search */}
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px'
            }}>
              <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                <i className="fas fa-search"></i> جستجو
              </label>
              <input
                type="text"
                placeholder="شماره دانشجویی، نام، نام خانوادگی، رشته، کد ملی..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>شماره دانشجویی</th>
                    <th>نام</th>
                    <th>نام خانوادگی</th>
                    <th>رشته</th>
                    <th>مقطع</th>
                    <th>کد ملی</th>
                  </tr>
                </thead>
                <tbody>
                  {filterStudents().map((student) => (
                    <tr key={student._id}>
                      <td>{student.student_id}</td>
                      <td>{student.name}</td>
                      <td>{student.family}</td>
                      <td>{student.major}</td>
                      <td>{student.level}</td>
                      <td>{student.national_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'festival' && (
          <div className="festival-section" data-testid="festival-section">
            <div className="section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <div>
                <h2><i className="fas fa-gift"></i> لیست ثبت‌نامی‌های جشن انجمن علمی</h2>
                <p>تعداد کل: {festivalRegistrations.length} نفر</p>
              </div>
              <button 
                className="btn-export"
                onClick={async () => {
                  try {
                    const dataForExport = festivalRegistrations.map(reg => ({
                      'نام': reg.first_name,
                      'نام خانوادگی': reg.last_name,
                      'شماره تماس': reg.phone,
                      'رشته تحصیلی': reg.field_of_study,
                      'علاقه‌مندی‌ها': reg.interests,
                      'تاریخ ثبت‌نام': new Date(reg.created_at).toLocaleString('fa-IR')
                    }));
                    
                    // Simple CSV export
                    const headers = Object.keys(dataForExport[0]);
                    const csvContent = [
                      headers.join(','),
                      ...dataForExport.map(row => headers.map(header => `"${row[header]}"`).join(','))
                    ].join('\\n');
                    
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = `festival_registrations_${new Date().toISOString().split('T')[0]}.csv`;
                    link.click();
                    
                    messageBox.success('فایل با موفقیت دانلود شد', 3000);
                  } catch (err) {
                    messageBox.error('خطا در دانلود فایل');
                  }
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-file-download"></i>
                دانلود CSV
              </button>
            </div>

            {/* Search */}
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px'
            }}>
              <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                <i className="fas fa-search"></i> جستجو
              </label>
              <input
                type="text"
                placeholder="نام، نام خانوادگی، شماره تماس، رشته..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>نام</th>
                    <th>نام خانوادگی</th>
                    <th>شماره تماس</th>
                    <th>رشته تحصیلی</th>
                    <th>علاقه‌مندی‌ها</th>
                    <th>تاریخ ثبت‌نام</th>
                  </tr>
                </thead>
                <tbody>
                  {festivalRegistrations
                    .filter(reg => 
                      !searchTerm || 
                      reg.first_name.includes(searchTerm) ||
                      reg.last_name.includes(searchTerm) ||
                      reg.phone.includes(searchTerm) ||
                      reg.field_of_study.includes(searchTerm) ||
                      reg.interests.includes(searchTerm)
                    )
                    .map((reg) => (
                    <tr key={reg.id}>
                      <td>{reg.first_name}</td>
                      <td>{reg.last_name}</td>
                      <td style={{direction: 'ltr', textAlign: 'center'}}>{reg.phone}</td>
                      <td>{reg.field_of_study}</td>
                      <td style={{maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={reg.interests}>
                        {reg.interests}
                      </td>
                      <td>{new Date(reg.created_at).toLocaleString('fa-IR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

        {/* Animation Event Section */}
        {activeTab === 'animation-event' && (
          <div className="event-section" data-testid="animation-event-section">
            {/* Statistics Cards */}
            {eventStats && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '30px'
              }}>
                <div className="stat-card" style={{
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  color: '#1F2937'
                }}>
                  <i className="fas fa-users" style={{fontSize: '2rem', marginBottom: '10px', color: '#6B7280'}}></i>
                  <h3 style={{color: '#111827', fontSize: '2rem', margin: '8px 0'}}>{eventStats.total_registrations}</h3>
                  <p style={{color: '#6B7280', fontSize: '0.9rem'}}>کل ثبت‌نام‌ها</p>
                </div>
                <div className="stat-card" style={{
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  color: '#1F2937'
                }}>
                  <i className="fas fa-university" style={{fontSize: '2rem', marginBottom: '10px', color: '#10B981'}}></i>
                  <h3 style={{color: '#111827', fontSize: '2rem', margin: '8px 0'}}>{eventStats.shomal_university}</h3>
                  <p style={{color: '#6B7280', fontSize: '0.9rem'}}>دانشگاه شمال</p>
                </div>
                <div className="stat-card" style={{
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  color: '#1F2937'
                }}>
                  <i className="fas fa-school" style={{fontSize: '2rem', marginBottom: '10px', color: '#3B82F6'}}></i>
                  <h3 style={{color: '#111827', fontSize: '2rem', margin: '8px 0'}}>{eventStats.other_universities}</h3>
                  <p style={{color: '#6B7280', fontSize: '0.9rem'}}>سایر دانشگاه‌ها</p>
                </div>
                <div className="stat-card" style={{
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  color: '#1F2937'
                }}>
                  <i className="fas fa-check-circle" style={{fontSize: '2rem', marginBottom: '10px', color: '#10B981'}}></i>
                  <h3 style={{color: '#111827', fontSize: '2rem', margin: '8px 0'}}>{eventStats.approved}</h3>
                  <p style={{color: '#6B7280', fontSize: '0.9rem'}}>تایید شده</p>
                </div>
                <div className="stat-card" style={{
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  color: '#1F2937'
                }}>
                  <i className="fas fa-clock" style={{fontSize: '2rem', marginBottom: '10px', color: '#F59E0B'}}></i>
                  <h3 style={{color: '#111827', fontSize: '2rem', margin: '8px 0'}}>{eventStats.pending}</h3>
                  <p style={{color: '#6B7280', fontSize: '0.9rem'}}>در انتظار</p>
                </div>
                <div className="stat-card" style={{
                  background: '#F3F4F6',
                  border: '1px solid #E5E7EB',
                  color: '#1F2937'
                }}>
                  <i className="fas fa-times-circle" style={{fontSize: '2rem', marginBottom: '10px', color: '#EF4444'}}></i>
                  <h3 style={{color: '#111827', fontSize: '2rem', margin: '8px 0'}}>{eventStats.rejected}</h3>
                  <p style={{color: '#6B7280', fontSize: '0.9rem'}}>رد شده</p>
                </div>
              </div>
            )}

            <div className="section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <div>
                <h2><i className="fas fa-film"></i> لیست ثبت‌نام‌های رویداد انیمیشن‌سازی</h2>
                <p>تعداد کل: {eventRegistrations.length} نفر</p>
              </div>
              <button 
                className="btn-export"
                onClick={() => handleExportExcel('event/export/approved')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500'
                }}
                title="دانلود لیست تایید شده‌ها"
              >
                <i className="fas fa-file-excel"></i>
                دانلود تایید شده‌ها (Excel)
              </button>
            </div>

            {/* Search and Filters */}
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr',
              gap: '16px'
            }}>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-search"></i> جستجو
                </label>
                <input
                  type="text"
                  placeholder="نام، شماره تماس..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-university"></i> دانشگاه
                </label>
                <select
                  value={dateFilter.start}
                  onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">همه</option>
                  <option value="shomal">شمال</option>
                  <option value="other">سایر</option>
                </select>
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px'}}>
                  <i className="fas fa-filter"></i> وضعیت
                </label>
                <select
                  value={dateFilter.end}
                  onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">همه</option>
                  <option value="approved">تایید شده</option>
                  <option value="pending">در انتظار</option>
                  <option value="rejected">رد شده</option>
                </select>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>نام و نام خانوادگی</th>
                    <th>شماره تماس</th>
                    <th>دانشگاه</th>
                    <th>شماره دانشجویی</th>
                    <th>وضعیت</th>
                    <th>رسید</th>
                    <th>تاریخ ثبت‌نام</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {eventRegistrations
                    .filter(reg => {
                      const matchSearch = !searchTerm || 
                        reg.first_name.includes(searchTerm) ||
                        reg.last_name.includes(searchTerm) ||
                        reg.phone.includes(searchTerm);
                      
                      const matchUniversity = !dateFilter.start || reg.university === dateFilter.start;
                      const matchStatus = !dateFilter.end || reg.status === dateFilter.end;
                      
                      return matchSearch && matchUniversity && matchStatus;
                    })
                    .map((reg) => (
                    <tr key={reg.id}>
                      <td style={{fontWeight: '500'}}>
                        {reg.first_name} {reg.last_name}
                      </td>
                      <td style={{direction: 'ltr', textAlign: 'center'}}>{reg.phone}</td>
                      <td>
                        {reg.university === 'shomal' ? (
                          <span style={{
                            backgroundColor: '#10B981',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            دانشگاه شمال
                          </span>
                        ) : (
                          <span style={{
                            backgroundColor: '#3B82F6',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            سایر دانشگاه‌ها
                          </span>
                        )}
                      </td>
                      <td style={{direction: 'ltr', textAlign: 'center'}}>
                        {reg.student_id || '-'}
                      </td>
                      <td>
                        {reg.status === 'approved' && (
                          <span style={{
                            backgroundColor: '#10B981',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            <i className="fas fa-check-circle"></i> تایید شده
                          </span>
                        )}
                        {reg.status === 'pending' && (
                          <span style={{
                            backgroundColor: '#F59E0B',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            <i className="fas fa-clock"></i> در انتظار
                          </span>
                        )}
                        {reg.status === 'rejected' && (
                          <span style={{
                            backgroundColor: '#EF4444',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            <i className="fas fa-times-circle"></i> رد شده
                          </span>
                        )}
                      </td>
                      <td>
                        {reg.receipt_image ? (
                          <button
                            onClick={() => {
                              const modal = document.createElement('div');
                              modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.9);display:flex;align-items:center;justify-content:center;z-index:9999;';
                              modal.onclick = () => document.body.removeChild(modal);
                              
                              const img = document.createElement('img');
                              img.src = reg.receipt_image;
                              img.style.cssText = 'max-width:90%;max-height:90%;border-radius:8px;';
                              
                              modal.appendChild(img);
                              document.body.appendChild(modal);
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#8B5CF6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            <i className="fas fa-image"></i> مشاهده
                          </button>
                        ) : (
                          <span style={{color: '#9CA3AF', fontSize: '12px'}}>-</span>
                        )}
                      </td>
                      <td>{new Date(reg.created_at).toLocaleString('fa-IR')}</td>
                      <td>
                        <div style={{display: 'flex', gap: '8px', justifyContent: 'center'}}>
                          {reg.status !== 'approved' && (
                            <button
                              onClick={async () => {
                                if (window.confirm('آیا از تایید این ثبت‌نام مطمئن هستید؟')) {
                                  try {
                                    const response = await fetch(`${BACKEND_URL}/api/event/registration/${reg.id}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Accept': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      mode: 'cors',
                                      credentials: 'include',
                                      body: JSON.stringify({ status: 'approved', admin_note: null })
                                    });
                                    
                                    if (response.ok) {
                                      messageBox.success('ثبت‌نام با موفقیت تایید شد', 2000);
                                      loadData();
                                    } else {
                                      throw new Error('خطا در تایید ثبت‌نام');
                                    }
                                  } catch (err) {
                                    messageBox.error('خطا در تایید ثبت‌نام');
                                  }
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#10B981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              title="تایید"
                            >
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          
                          {reg.status !== 'rejected' && (
                            <button
                              onClick={async () => {
                                const reason = window.prompt('دلیل رد ثبت‌نام را وارد کنید:');
                                if (reason) {
                                  try {
                                    const response = await fetch(`${BACKEND_URL}/api/event/registration/${reg.id}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Accept': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      mode: 'cors',
                                      credentials: 'include',
                                      body: JSON.stringify({ status: 'rejected', admin_note: reason })
                                    });
                                    
                                    if (response.ok) {
                                      messageBox.success('ثبت‌نام رد شد', 2000);
                                      loadData();
                                    } else {
                                      throw new Error('خطا در رد ثبت‌نام');
                                    }
                                  } catch (err) {
                                    messageBox.error('خطا در رد ثبت‌نام');
                                  }
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#EF4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              title="رد"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                          
                          <button
                            onClick={async () => {
                              if (window.confirm('آیا از حذف این ثبت‌نام مطمئن هستید؟')) {
                                try {
                                  const response = await fetch(`${BACKEND_URL}/api/event/registration/${reg.id}`, {
                                    method: 'DELETE',
                                    headers: {
                                      'Accept': 'application/json',
                                      'Authorization': `Bearer ${token}`
                                    },
                                    mode: 'cors',
                                    credentials: 'include'
                                  });
                                  
                                  if (response.ok) {
                                    messageBox.success('ثبت‌نام حذف شد', 2000);
                                    loadData();
                                  } else {
                                    throw new Error('خطا در حذف ثبت‌نام');
                                  }
                                } catch (err) {
                                  messageBox.error('خطا در حذف ثبت‌نام');
                                }
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#6B7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            title="حذف"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {eventRegistrations.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#9CA3AF'
              }}>
                <i className="fas fa-inbox" style={{fontSize: '3rem', marginBottom: '16px'}}></i>
                <p>هنوز ثبت‌نامی وجود ندارد</p>
              </div>
            )}
          </div>
        )}

        {/* Tikka Event Section */}
        {activeTab === 'tikka-event' && (
          <div className="event-section" data-testid="tikka-event-section">
            {/* Statistics Cards */}
            {tikkaStats && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '30px'
              }}>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
                  border: 'none',
                  color: 'white'
                }}>
                  <i className="fas fa-users" style={{fontSize: '2rem', marginBottom: '10px', color: 'rgba(255,255,255,0.8)'}}></i>
                  <h3 style={{color: 'white', fontSize: '2rem', margin: '8px 0'}}>{tikkaStats.total_registrations}</h3>
                  <p style={{color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem'}}>کل ثبت‌نام‌ها</p>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
                  border: 'none',
                  color: 'white'
                }}>
                  <i className="fas fa-university" style={{fontSize: '2rem', marginBottom: '10px', color: 'rgba(255,255,255,0.8)'}}></i>
                  <h3 style={{color: 'white', fontSize: '2rem', margin: '8px 0'}}>{tikkaStats.shomal_university}</h3>
                  <p style={{color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem'}}>دانشگاه شمال</p>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #0284C7 0%, #0EA5E9 100%)',
                  border: 'none',
                  color: 'white'
                }}>
                  <i className="fas fa-school" style={{fontSize: '2rem', marginBottom: '10px', color: 'rgba(255,255,255,0.8)'}}></i>
                  <h3 style={{color: 'white', fontSize: '2rem', margin: '8px 0'}}>{tikkaStats.other_universities}</h3>
                  <p style={{color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem'}}>سایر دانشگاه‌ها</p>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #16A34A 0%, #22C55E 100%)',
                  border: 'none',
                  color: 'white'
                }}>
                  <i className="fas fa-check-circle" style={{fontSize: '2rem', marginBottom: '10px', color: 'rgba(255,255,255,0.8)'}}></i>
                  <h3 style={{color: 'white', fontSize: '2rem', margin: '8px 0'}}>{tikkaStats.approved}</h3>
                  <p style={{color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem'}}>تایید شده</p>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
                  border: 'none',
                  color: 'white'
                }}>
                  <i className="fas fa-clock" style={{fontSize: '2rem', marginBottom: '10px', color: 'rgba(255,255,255,0.8)'}}></i>
                  <h3 style={{color: 'white', fontSize: '2rem', margin: '8px 0'}}>{tikkaStats.pending}</h3>
                  <p style={{color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem'}}>در انتظار</p>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
                  border: 'none',
                  color: 'white'
                }}>
                  <i className="fas fa-times-circle" style={{fontSize: '2rem', marginBottom: '10px', color: 'rgba(255,255,255,0.8)'}}></i>
                  <h3 style={{color: 'white', fontSize: '2rem', margin: '8px 0'}}>{tikkaStats.rejected}</h3>
                  <p style={{color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem'}}>رد شده</p>
                </div>
              </div>
            )}

            <div className="section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <div>
                <h2><i className="fas fa-rocket"></i> لیست ثبت‌نام‌های رویداد پلتفرم تیکا</h2>
                <p style={{color: '#6B7280', marginTop: '8px'}}>از دانشگاه تا بزرگترین استارتاپ آموزش زبان - ۱۹ آذر ۱۴۰۴</p>
                <p>تعداد کل: {tikkaRegistrations.length} نفر</p>
              </div>
              <button 
                className="btn-export"
                onClick={() => handleExportExcel('tikka/export/approved')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#0D9488',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500'
                }}
                title="دانلود لیست تایید شده‌ها"
              >
                <i className="fas fa-file-excel"></i>
                دانلود تایید شده‌ها (Excel)
              </button>
            </div>

            {/* Search and Filters */}
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f0fdfa',
              borderRadius: '12px',
              border: '1px solid #99f6e4',
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr',
              gap: '16px'
            }}>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#0D9488', fontSize: '14px', fontWeight: '600'}}>
                  <i className="fas fa-search"></i> جستجو
                </label>
                <input
                  type="text"
                  placeholder="نام، شماره تماس..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #5eead4',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#0D9488', fontSize: '14px', fontWeight: '600'}}>
                  <i className="fas fa-university"></i> دانشگاه
                </label>
                <select
                  value={dateFilter.start}
                  onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #5eead4',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">همه</option>
                  <option value="shomal">شمال</option>
                  <option value="other">سایر</option>
                </select>
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '8px', color: '#0D9488', fontSize: '14px', fontWeight: '600'}}>
                  <i className="fas fa-filter"></i> وضعیت
                </label>
                <select
                  value={dateFilter.end}
                  onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #5eead4',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">همه</option>
                  <option value="approved">تایید شده</option>
                  <option value="pending">در انتظار</option>
                  <option value="rejected">رد شده</option>
                </select>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr style={{backgroundColor: '#0D9488', color: 'white'}}>
                    <th>نام و نام خانوادگی</th>
                    <th>شماره تماس</th>
                    <th>دانشگاه</th>
                    <th>شماره دانشجویی</th>
                    <th>وضعیت</th>
                    <th>رسید</th>
                    <th>تاریخ ثبت‌نام</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {tikkaRegistrations
                    .filter(reg => {
                      const matchSearch = !searchTerm || 
                        reg.first_name.includes(searchTerm) ||
                        reg.last_name.includes(searchTerm) ||
                        reg.phone.includes(searchTerm);
                      
                      const matchUniversity = !dateFilter.start || reg.university === dateFilter.start;
                      const matchStatus = !dateFilter.end || reg.status === dateFilter.end;
                      
                      return matchSearch && matchUniversity && matchStatus;
                    })
                    .map((reg) => (
                    <tr key={reg.id}>
                      <td style={{fontWeight: '500'}}>
                        {reg.first_name} {reg.last_name}
                      </td>
                      <td style={{direction: 'ltr', textAlign: 'center'}}>{reg.phone}</td>
                      <td>
                        {reg.university === 'shomal' ? (
                          <span style={{
                            backgroundColor: '#10B981',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            دانشگاه شمال
                          </span>
                        ) : (
                          <span style={{
                            backgroundColor: '#0EA5E9',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            سایر دانشگاه‌ها
                          </span>
                        )}
                      </td>
                      <td style={{direction: 'ltr', textAlign: 'center'}}>
                        {reg.student_id || '-'}
                      </td>
                      <td>
                        {reg.status === 'approved' && (
                          <span style={{
                            backgroundColor: '#10B981',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            <i className="fas fa-check-circle"></i> تایید شده
                          </span>
                        )}
                        {reg.status === 'pending' && (
                          <span style={{
                            backgroundColor: '#F59E0B',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            <i className="fas fa-clock"></i> در انتظار
                          </span>
                        )}
                        {reg.status === 'rejected' && (
                          <span style={{
                            backgroundColor: '#EF4444',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            <i className="fas fa-times-circle"></i> رد شده
                          </span>
                        )}
                      </td>
                      <td>
                        {reg.receipt_image ? (
                          <button
                            onClick={() => {
                              const modal = document.createElement('div');
                              modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.9);display:flex;align-items:center;justify-content:center;z-index:9999;';
                              modal.onclick = () => document.body.removeChild(modal);
                              
                              const img = document.createElement('img');
                              img.src = reg.receipt_image;
                              img.style.cssText = 'max-width:90%;max-height:90%;border-radius:8px;';
                              
                              modal.appendChild(img);
                              document.body.appendChild(modal);
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#8B5CF6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            <i className="fas fa-image"></i> مشاهده
                          </button>
                        ) : (
                          <span style={{color: '#9CA3AF', fontSize: '12px'}}>-</span>
                        )}
                      </td>
                      <td>{new Date(reg.created_at).toLocaleString('fa-IR')}</td>
                      <td>
                        <div style={{display: 'flex', gap: '8px', justifyContent: 'center'}}>
                          {reg.status !== 'approved' && (
                            <button
                              onClick={async () => {
                                if (window.confirm('آیا از تایید این ثبت‌نام مطمئن هستید؟')) {
                                  try {
                                    const response = await fetch(`${BACKEND_URL}/api/tikka/registration/${reg.id}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Accept': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      mode: 'cors',
                                      credentials: 'include',
                                      body: JSON.stringify({ status: 'approved', admin_note: null })
                                    });
                                    
                                    if (response.ok) {
                                      messageBox.success('ثبت‌نام با موفقیت تایید شد', 2000);
                                      loadData();
                                    } else {
                                      throw new Error('خطا در تایید ثبت‌نام');
                                    }
                                  } catch (err) {
                                    messageBox.error('خطا در تایید ثبت‌نام');
                                  }
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#10B981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              title="تایید"
                            >
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          
                          {reg.status !== 'rejected' && (
                            <button
                              onClick={async () => {
                                const reason = window.prompt('دلیل رد ثبت‌نام را وارد کنید:');
                                if (reason) {
                                  try {
                                    const response = await fetch(`${BACKEND_URL}/api/tikka/registration/${reg.id}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Accept': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      mode: 'cors',
                                      credentials: 'include',
                                      body: JSON.stringify({ status: 'rejected', admin_note: reason })
                                    });
                                    
                                    if (response.ok) {
                                      messageBox.success('ثبت‌نام رد شد', 2000);
                                      loadData();
                                    } else {
                                      throw new Error('خطا در رد ثبت‌نام');
                                    }
                                  } catch (err) {
                                    messageBox.error('خطا در رد ثبت‌نام');
                                  }
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#EF4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              title="رد"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                          
                          <button
                            onClick={async () => {
                              if (window.confirm('آیا از حذف این ثبت‌نام مطمئن هستید؟')) {
                                try {
                                  const response = await fetch(`${BACKEND_URL}/api/tikka/registration/${reg.id}`, {
                                    method: 'DELETE',
                                    headers: {
                                      'Accept': 'application/json',
                                      'Authorization': `Bearer ${token}`
                                    },
                                    mode: 'cors',
                                    credentials: 'include'
                                  });
                                  
                                  if (response.ok) {
                                    messageBox.success('ثبت‌نام حذف شد', 2000);
                                    loadData();
                                  } else {
                                    throw new Error('خطا در حذف ثبت‌نام');
                                  }
                                } catch (err) {
                                  messageBox.error('خطا در حذف ثبت‌نام');
                                }
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#6B7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            title="حذف"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {tikkaRegistrations.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#9CA3AF'
              }}>
                <i className="fas fa-inbox" style={{fontSize: '3rem', marginBottom: '16px'}}></i>
                <p>هنوز ثبت‌نامی وجود ندارد</p>
              </div>
            )}
          </div>
        )}


      {/* Edit Vote Modal */}
      {editingVote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{marginBottom: '20px'}}>ویرایش رای</h3>
            <div style={{marginBottom: '16px'}}>
              <p><strong>دانشجو:</strong> {editingVote.student_name}</p>
              <p><strong>شماره دانشجویی:</strong> {editingVote.student_id}</p>
              <p><strong>رشته:</strong> {editingVote.student_major}</p>
            </div>
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: 'bold'}}>
                نامزدهای انتخابی (با کاما جدا کنید):
              </label>
              <input
                type="text"
                value={editCandidates}
                onChange={(e) => setEditCandidates(e.target.value)}
                placeholder="مثال: 1,2,3,4,5"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <p style={{fontSize: '12px', color: '#6b7280', marginTop: '8px'}}>
                حداقل 1 و حداکثر 7 نامزد را با کاما از هم جدا کنید
              </p>
            </div>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  setEditingVote(null);
                  setEditCandidates([]);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                انصراف
              </button>
              <button
                onClick={handleSaveEdit}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-save"></i> ذخیره تغییرات
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;